'use client';

import { Popover as RadixPopover } from 'radix-ui';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from './cn';

/**
 * What every anchored panel shares, menus and popovers alike: the surface, its hairline, its layer
 * and the open/close animation (it grows from its trigger). Never a backdrop: an anchored panel is
 * not modal, a click outside or Escape closes it.
 */
export const panelSurfaceClass =
  'z-[70] border border-crow-line bg-crow-surface text-crow-text data-[state=closed]:animate-crow-menu-out data-[state=open]:animate-crow-menu-in';

/**
 * The surface of what floats over the page and holds free content (a form, a picker, a detail):
 * elevation 1, like the menus, and it grows from its trigger. No padding: the content sets it.
 */
export const popoverContentClass = cn(
  panelSurfaceClass,
  'rounded-crow-panel shadow-crow-1 outline-none',
);

/**
 * A large panel opened from the app's frame (the product switcher, the notifications): the same
 * anchored surface at elevation 3, with larger corners. Width and padding stay with each panel.
 */
export const popoverPanelClass = cn(panelSurfaceClass, 'rounded-xl shadow-crow-3 outline-none');

/**
 * Free content opened from a button. Escape and a click outside close it, focus goes back to the
 * trigger. Controlled with `open` and `onOpenChange`, or on its own.
 */
export function Popover({
  trigger,
  children,
  open,
  defaultOpen,
  onOpenChange,
  align = 'center',
  side = 'bottom',
  sideOffset = 6,
  label,
  className,
}: {
  /** The element that opens the popover, usually a Button or an IconButton. */
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  /** Accessible name of the popover, when its content has no heading. */
  label?: string;
  className?: string;
}) {
  return (
    <RadixPopover.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          side={side}
          sideOffset={sideOffset}
          aria-label={label}
          className={cn(popoverContentClass, 'w-72 p-3', className)}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/** The parts, for a popover `Popover` cannot express (its own focus, collisions, a ref). */
export const PopoverRoot = RadixPopover.Root;
export const PopoverTrigger = RadixPopover.Trigger;
export const PopoverClose = RadixPopover.Close;
export const PopoverAnchor = RadixPopover.Anchor;

/**
 * A popover's content, portalled: `popoverContentClass`, or `popoverPanelClass` for a large panel
 * of the app's frame (`variant="panel"`).
 */
export function PopoverContent({
  className,
  variant = 'default',
  align = 'start',
  sideOffset = 6,
  ...props
}: ComponentPropsWithRef<typeof RadixPopover.Content> & { variant?: 'default' | 'panel' }) {
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(variant === 'panel' ? popoverPanelClass : popoverContentClass, className)}
        {...props}
      />
    </RadixPopover.Portal>
  );
}
